/**
 * React Query v4 Configuration
 * Centralized configuration for caching, retries, and stale time
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 2 minutes before considering it stale
      staleTime: 2 * 60 * 1000,
      // Keep unused data in cache for 10 minutes (gcTime replaces cacheTime in v4+)
      gcTime: 10 * 60 * 1000,
      // Retry failed requests with smart logic
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      // Disable refetch on window focus for Kanban board (reduces unnecessary API calls)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
      // Only refetch on mount if data is stale
      refetchOnMount: 'stale',
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

/**
 * Query Keys Factory
 * Centralized query key management for type safety and consistency
 */
export const queryKeys = {
  // Tasks board queries
  tasks: {
    all: ['tasks'],
    board: (taskStages) => ['tasks', 'board', taskStages],
    column: (columnId, page) => ['tasks', 'column', columnId, page],
    infiniteColumn: (columnId) => ['tasks', 'column-infinite', columnId],
    manage: (columnId) => ['tasks', 'manage-column', columnId],
  },
  // Practice areas
  practiceAreas: {
    all: ['practice-areas'],
    list: () => ['practice-areas', 'list'],
  },
  // Task stages
  taskStages: {
    all: ['task-stages'],
    list: () => ['task-stages', 'list'],
  },
  // Users
  users: {
    all: ['users'],
    list: (status) => ['users', 'list', status],
  },
};
